import React, { useEffect, useMemo, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import { REPORT_QUERY_PARAMS } from "@sscs/report/constants";
import {
  ReportContext,
  SDLCReport,
  useRestoreReportSlices,
  useRiskAssessmentClick,
} from "@sscs/report/report";
import { reportCleared } from "@sscs/report/store";
import {
  ExportReportType,
  MalwareFindingComponentProps,
  SDLCMetadata,
  ScanReportType,
  isErrorMessage,
} from "@sscs/types";
import { ErrorBoundary, Page, PageNotFound } from "@sscs/ui";
import { CommunityVersions } from "Components";
import {
  FULL_REPORT_PATH,
  PAGE_NOT_FOUND_ROUTE,
  PERMISSION,
  REPORT_EXPORT_FILE_NAME,
  REPORT_TYPE_REPRO,
} from "Constants";
import { useAllowedTo, useAppDispatch, useReports } from "Hooks";
import {
  downloadFile,
  notificationsForReportDownload,
  useGetLatestReportQuery,
} from "Services";
import { updateReport } from "Store/reports.slice";
import FileStreamReportHeader from "../components/FileStreamReportHeader/FileStreamReportHeader";
import MalwareFinding from "../components/MalwareFinding/MalwareFinding";
import NotificationHandler from "../components/NotificationHandler";
import ProjectReportHeader from "../components/ProjectReportHeader/ProjectReportHeader";
import ReportDeleted from "../components/ReportDeleted";
import StatusCheckLink from "../components/StatusCheckLink";
import { REPORT_EXPORT_ENDPOINT } from "../utils";
import styles from "./Report.module.scss";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const Report = (): React.JSX.Element | null => {
  const dispatch = useAppDispatch();

  const { openedReportKey, deletedReports } = useReports();
  const {
    groupId,
    projectName,
    packageName,
    version,
    "*": restOfThePath,
  } = useParams();

  const [searchParams] = useSearchParams();
  const build = searchParams.get(REPORT_QUERY_PARAMS.build);
  const diffWith = searchParams.get(REPORT_QUERY_PARAMS.diff_with);
  const validDiffWith =
    diffWith && UUID_PATTERN.test(diffWith) ? diffWith : undefined;
  const riskCategory = searchParams.get(REPORT_QUERY_PARAMS.risk_category);
  const riskViolations = useMemo(
    () =>
      searchParams.get(REPORT_QUERY_PARAMS.risk_violations)?.split(",") ?? [],
    [searchParams],
  );
  const selectedCheck =
    searchParams.get(REPORT_QUERY_PARAMS.build) === REPORT_TYPE_REPRO
      ? "scan-repro"
      : "scan-version";
  const {
    data: report,
    currentData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetLatestReportQuery(
    groupId && projectName && packageName && version
      ? {
          groupId: groupId,
          build: build,
          projectName: projectName,
          packageName: packageName,
          version: version,
          diffWith: validDiffWith,
        }
      : skipToken,
  );

  const { linkTarget, handleRiskCountClick } = useRiskAssessmentClick({
    reportId: report?.latest_report_id,
    diffId: report?.latest_diff_report_id,
    riskCategory: riskCategory as SDLCMetadata.assessment_type_t,
    violations: riskViolations,
    selectedCheck: selectedCheck,
  });

  const isFileStream = report?.file_info.file_type === "filestream";

  // Each export format is gated on its own backend permission so a user only
  // sees the formats they are authorized to export (mirrors the per-format
  // menu items in ReportExportButton).
  const canExportSarif = useAllowedTo({
    permissions: [PERMISSION.EXPORT_SARIF_REPORT],
  });
  const canExportCycloneDx = useAllowedTo({
    permissions: [PERMISSION.EXPORT_CYCLONEDX_REPORT],
  });
  const canExportSpdx = useAllowedTo({
    permissions: [PERMISSION.EXPORT_SPDX_REPORT],
  });
  const canExportCve = useAllowedTo({
    permissions: [PERMISSION.EXPORT_RL_CVE_REPORT],
  });
  const canExportJson = useAllowedTo({
    permissions: [PERMISSION.EXPORT_RL_JSON_REPORT],
  });
  const canExportUri = useAllowedTo({
    permissions: [PERMISSION.EXPORT_RL_URI_REPORT],
  });
  const canExportSummaryPdf = useAllowedTo({
    permissions: [PERMISSION.EXPORT_RL_SUMMARY_PDF],
  });
  const canExportSafe = useAllowedTo({
    permissions: [PERMISSION.EXPORT_RL_SAFE_ARCHIVE],
  });

  const canDownloadPackage = useAllowedTo({
    permissions: [
      PERMISSION.MODIFY_VERSION_APPROVAL,
      PERMISSION.DOWNLOAD_VERSIONS,
    ],
  });

  const canMoveToProject = useAllowedTo({
    permissions: [PERMISSION.UPLOAD_VERSION],
  });

  const canGenerateReportLink = useAllowedTo({
    permissions: [PERMISSION.SHARE_REPORT],
  });

  useRestoreReportSlices(
    openedReportKey,
    isFetching && !currentData
      ? undefined
      : (report?.latest_report_id ?? undefined),
  );

  const [activeExport, setActiveExport] = useState<
    Record<ExportReportType, boolean>
  >({
    cycloneDX: false,
    json: false,
    cve: false,
    safe: false,
    sarif: false,
    spdx: false,
    summaryPdf: false,
    uri: false,
  });

  useEffect(() => {
    return () => {
      // cleanup report state when report is unmounted
      dispatch(reportCleared());
    };
  }, [dispatch]);

  useEffect(() => {
    return () => {
      if (openedReportKey) {
        dispatch(
          updateReport({
            reportId: openedReportKey,
            report: {
              goToPage: restOfThePath,
              searchParams: searchParams.toString(),
            },
          }),
        );
      }
    };
  }, [restOfThePath, searchParams, dispatch, openedReportKey]);

  const navigate = useNavigate();

  useEffect(() => {
    if (!report?.latest_report_id || !riskCategory || !linkTarget) {
      return;
    }

    handleRiskCountClick();

    const newSearchParams = new URLSearchParams(searchParams.toString());

    // We don't want to keep risk in the URL once user has been redirected to show risk they clicked on
    // from file stream or version expanded row
    newSearchParams.delete(REPORT_QUERY_PARAMS.risk_category);

    navigate(
      { pathname: linkTarget, search: newSearchParams.toString() },
      { replace: true },
    );
  }, [
    riskCategory,
    report?.latest_report_id,
    linkTarget,
    handleRiskCountClick,
    navigate,
    searchParams,
  ]);

  const reportType: ScanReportType = build === "repro" ? "repro" : "version";

  if (isError) {
    if ("status" in error && error.status === 404) {
      return <Navigate to={`/${PAGE_NOT_FOUND_ROUTE}`} replace />;
    } else if ("data" in error && isErrorMessage(error.data)) {
      return (
        <PageNotFound
          title="Something went wrong"
          byline={error.data.data.error}
        />
      );
    } else {
      return <PageNotFound title="Something went wrong" />;
    }
  }

  if (isLoading || (isFetching && !currentData))
    return (
      <Page.Root>
        <Page.Body status={{ status: "LOADING" }} />
      </Page.Root>
    );

  if (!report || !report.latest_report_id) return null;

  const handleReportExport = (reportType: ExportReportType) => {
    setActiveExport((prevState) => ({ ...prevState, [reportType]: true }));
    downloadFile(
      `/reports/v1/exports/${report.latest_report_id}/${REPORT_EXPORT_ENDPOINT[reportType]}`,
      REPORT_EXPORT_FILE_NAME[reportType],
      notificationsForReportDownload(reportType),
    ).then(() => {
      setActiveExport((prevState) => ({ ...prevState, [reportType]: false }));
    });
  };

  return (
    <div className={styles["report-shell"]} data-page-shell>
      <ErrorBoundary>
        <ReportContext.Provider
          value={{
            routerRootPath: FULL_REPORT_PATH,
            reportId: report.latest_report_id,
            diffId: report.latest_diff_report_id ?? "",
            selectedCheck: selectedCheck,
            roles: {
              canDownloadPackage: canDownloadPackage,
              canExportCve: canExportCve,
              canExportCycloneDx: canExportCycloneDx,
              canExportJson: canExportJson,
              canExportSafe: canExportSafe,
              canExportSarif: canExportSarif,
              canExportSpdx: canExportSpdx,
              canExportSummaryPdf: canExportSummaryPdf,
              canExportUri: canExportUri,
            },
            activeExport,
            supportsYaraPolicies: false,
            onReportExport: handleReportExport,
            MalwareFindingComponent: ({
              isAnalystVetted,
            }: MalwareFindingComponentProps) => (
              <MalwareFinding
                isFileStream={isFileStream}
                isAnalystVetted={isAnalystVetted}
              />
            ),
            StatusCheckLink: StatusCheckLink,
            CommunityVersions: CommunityVersions,
          }}
        >
          <NotificationHandler
            key={report.latest_report_id}
            fileId={report.file_id}
            isReportDeleted={
              openedReportKey ? deletedReports.includes(openedReportKey) : false
            }
            isFileStream={isFileStream}
          >
            <>
              {isFileStream ? (
                <FileStreamReportHeader
                  fileId={report.file_id}
                  canMoveToProject={canMoveToProject}
                />
              ) : (
                <ProjectReportHeader
                  versionId={report.file_id}
                  purlComponents={{
                    projectName: projectName ?? "",
                    packageName: packageName ?? "",
                    version: version ?? "",
                  }}
                  reportStats={report.shared_reports_stats}
                  reportType={reportType}
                  canGenerateReportLink={canGenerateReportLink}
                />
              )}
              <SDLCReport />
            </>
          </NotificationHandler>
          <ReportDeleted />
        </ReportContext.Provider>
      </ErrorBoundary>
    </div>
  );
};

export default Report;
